"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthProvider = void 0;
const react_1 = __importStar(require("react"));
const AuthContext_1 = require("./AuthContext");
const AuthProvider = ({ children, sessionManager, getCurrentLocation, onLogout, onLogin, }) => {
    const currentLocation = getCurrentLocation();
    // Mirror SessionManager state into React state via LAYER 2 subscription.
    const [state, setState] = (0, react_1.useState)(() => sessionManager.getState());
    // True from the moment logoutUser/signOutFederated is invoked until it
    // resolves. Not derived from SessionManager state — its signOut() path
    // never flips isValidatingSession true (only false), so there's no
    // existing signal during the host's `onLogout` callback window. Hosts
    // gate any UI that reacts to torn-down state (socket overlays, etc.)
    // on this flag to avoid flashes mid-logout.
    const [isLoggingOut, setIsLoggingOut] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        return sessionManager.subscribe(setState);
    }, [sessionManager]);
    // On every route change, re-check (silent if already authenticated).
    (0, react_1.useEffect)(() => {
        if (state.isLoggedIn) {
            sessionManager.refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation]);
    // Cross-app auth detection: when the tab becomes visible (user returns
    // after a sign-in or sign-out in another app under the same cookie
    // domain), re-check the session. Refresh() reads the shared cookies via
    // the adapter and flips state to match reality. If state transitioned:
    //   logged-in → logged-out  →  the other app signed out: fire onLogout
    //   logged-out → logged-in  →  the other app signed in:  fire onLogin
    //
    // Browsers don't fire change events on cookies, so this is the only way
    // to surface cross-app auth changes without polling.
    //
    // No double-fire with user-initiated flows: signIn/signOut in this tab
    // don't change visibilityState, so this listener doesn't run during them.
    (0, react_1.useEffect)(() => {
        if (typeof document === "undefined")
            return;
        const onVisible = () => __awaiter(void 0, void 0, void 0, function* () {
            if (document.visibilityState !== "visible")
                return;
            const wasLoggedIn = sessionManager.getState().isLoggedIn;
            yield sessionManager.refresh();
            const nowLoggedIn = sessionManager.getState().isLoggedIn;
            // onLogout / onLogin errors deliberately propagate — an unhandled
            // rejection here lets Sentry catch a failing host callback that
            // would otherwise disappear silently. Trade-off: if the host's
            // teardown throws, sessionManager state is already fresh from the
            // refresh() above but any host-side cleanup after the throw is
            // skipped. Bug visibility > partial-cleanup convenience.
            if (wasLoggedIn && !nowLoggedIn) {
                yield (onLogout === null || onLogout === void 0 ? void 0 : onLogout());
            }
            else if (!wasLoggedIn && nowLoggedIn) {
                yield (onLogin === null || onLogin === void 0 ? void 0 : onLogin());
            }
        });
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [sessionManager, onLogout, onLogin]);
    const signIn = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        yield sessionManager.signIn();
    }), [sessionManager]);
    const logoutUser = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        // Flip isLoggingOut BEFORE the host's onLogout runs — that's where
        // the host typically tears down sockets / domain state. UI gated on
        // !isLoggingOut needs to see the flag flip before any of that fires.
        setIsLoggingOut(true);
        try {
            // Host cleanup runs first so it can drive its own teardown (e.g.
            // domain transition, server-side session invalidation) while the
            // user is still authenticated. If onLogout throws, we DON'T swallow
            // it — the exception propagates as an unhandled promise rejection
            // so Sentry picks it up. Trade-off: sessionManager.signOut() below
            // won't run in the failure path, leaving the user in an
            // inconsistent state (tokens still present, UI thinks logout
            // succeeded). Accepted per PR review — bug visibility on logout
            // failures beats silently masking them; the failure path is rare
            // enough that "still logged in on next reload" is a survivable UX.
            yield (onLogout === null || onLogout === void 0 ? void 0 : onLogout());
            yield sessionManager.signOut();
        }
        finally {
            // Reset on completion or failure. By success, the consumer subtree
            // has typically unmounted (isLoggedIn flipped to false); reset
            // matters for the failure path so a retry isn't stuck.
            setIsLoggingOut(false);
        }
    }), [sessionManager, onLogout]);
    const signOutFederated = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoggingOut(true);
        try {
            // Same onLogout-first ordering + no-swallow semantics as
            // logoutUser. The difference is the adapter call:
            // signOutFederated also clears the identity provider's session
            // cookie (on native, opens /oauth2/logout in the in-app browser).
            // Falls back to local-only on adapters that don't implement
            // signOutFederated (web does this implicitly — Amplify's sign-out
            // is already federated).
            yield (onLogout === null || onLogout === void 0 ? void 0 : onLogout());
            yield sessionManager.signOutFederated();
        }
        finally {
            setIsLoggingOut(false);
        }
    }), [sessionManager, onLogout]);
    const value = {
        sessionManager,
        isLoggedIn: state.isLoggedIn,
        isValidatingSession: state.isValidatingSession,
        isLoggingOut,
        error: state.error,
        signIn,
        logoutUser,
        signOutFederated,
    };
    return react_1.default.createElement(AuthContext_1.AuthContext.Provider, { value: value }, children);
};
exports.AuthProvider = AuthProvider;
